import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  KeyRound, Loader2, CheckCircle2, XCircle, Save, Zap, Lock,
  Link2, Plug, AlertTriangle, Copy, Facebook, Linkedin, RefreshCw,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdStatusRow({ name, icon: Icon, tone, cfg, onConnect, onRefresh, busy }) {
  const connected = cfg?.connected;
  const configured = cfg?.configured;
  const needsAuth = cfg?.needs_auth;
  return (
    <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 flex items-center justify-between gap-3" data-testid={`ad-status-${name.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${tone}1A` }}>
          <Icon className="w-4 h-4" style={{ color: tone }} />
        </div>
        <div>
          <div className="text-[#1A1A1A] text-sm font-medium">{name} Ads</div>
          <div className="text-[11px] flex items-center gap-1.5 mt-0.5">
            {!configured ? (
              <span className="text-[#8A857A] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Keys not set</span>
            ) : connected ? (
              <span className="text-[#0E6B3F] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Connected · {cfg.account_id}</span>
            ) : (
              <span className="text-[#6B5600] inline-flex items-center gap-1"><Plug className="w-3 h-3" /> Keys saved — needs Connect</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {configured && needsAuth && onConnect && (
          <Button size="sm" onClick={onConnect} className="text-[#1A1A1A] font-medium" style={{ background: tone }} data-testid={`ad-status-connect-${name.toLowerCase()}`}>
            <Link2 className="w-4 h-4 mr-1" /> Connect
          </Button>
        )}
        {configured && connected && onRefresh && (
          <Button size="sm" variant="outline" onClick={onRefresh} disabled={busy} className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid={`ad-status-refresh-${name.toLowerCase()}`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sync
          </Button>
        )}
      </div>
    </div>
  );
}

export default function IntegrationsKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState("");
  const [testing, setTesting] = useState("");
  const [testResult, setTestResult] = useState({});
  const [adStatus, setAdStatus] = useState(null);
  const [adBusy, setAdBusy] = useState("");

  const loadAdStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/marketing/ads/status`);
      setAdStatus(r.data);
    } catch { /* non-blocking */ }
  }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/settings/keys`);
      setKeys(r.data.keys || []);
    } catch { toast.error("Failed to load keys"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadAdStatus(); }, [loadAdStatus]);

  // Re-check ad connection when returning from the LinkedIn OAuth popup.
  useEffect(() => {
    const onFocus = () => loadAdStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadAdStatus]);

  const connectLinkedIn = async () => {
    try {
      const r = await axios.get(`${API}/marketing/ads/linkedin/start`);
      window.open(r.data.authorize_url, "linkedin_oauth", "width=600,height=740");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start LinkedIn connect"); }
  };

  const syncAds = async (platform) => {
    setAdBusy(platform);
    try {
      const r = await axios.post(`${API}/marketing/ads/${platform}/refresh`, {});
      toast.success(`${platform === "meta" ? "Meta" : "LinkedIn"}: synced ${r.data.rows} row(s)`);
      await loadAdStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setAdBusy(""); }
  };

  const copyRedirect = () => {
    const uri = adStatus?.linkedin?.redirect_uri;
    if (uri) { navigator.clipboard.writeText(uri); toast.success("Redirect URL copied"); }
  };

  const save = async (name) => {
    setSaving(name);
    try {
      await axios.put(`${API}/settings/keys`, { name, value: drafts[name] || "" });
      toast.success("Saved");
      setDrafts(d => { const c = { ...d }; delete c[name]; return c; });
      await load();
      if (name.startsWith("META_") || name.startsWith("LINKEDIN_")) await loadAdStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(""); }
  };

  const test = async (provider) => {
    setTesting(provider);
    setTestResult(r => ({ ...r, [provider]: null }));
    try {
      const r = await axios.post(`${API}/settings/keys/test`, { provider });
      setTestResult(x => ({ ...x, [provider]: r.data.healthy ? { ok: true } : { ok: false, detail: r.data.detail } }));
      toast[r.data.healthy ? "success" : "error"](r.data.healthy ? `${provider} is working` : `${provider} failed: ${r.data.detail || ""}`);
    } catch (e) { toast.error("Test failed"); }
    finally { setTesting(""); }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#8A857A] py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading keys…</div>;

  const groups = [...new Set(keys.map(k => k.group))];

  return (
    <div className="space-y-6" data-testid="integrations-keys">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center"><KeyRound className="w-5 h-5 text-[#8A6D00]" /></div>
        <div>
          <h3 className="text-lg font-semibold text-[#1A1A1A]">Integrations &amp; Keys</h3>
          <p className="text-sm text-[#59554D]">Manage AI engine and ad-platform credentials. Stored encrypted; changes apply immediately.</p>
        </div>
      </div>

      {groups.map(group => (
        <div key={group} className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">{group}</div>
          {keys.filter(k => k.group === group).map(k => {
            const tr = testResult[k.test];
            return (
              <Card key={k.name} className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid={`key-row-${k.name}`}>
                <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="md:w-56 shrink-0">
                    <div className="text-[#1A1A1A] text-sm font-medium flex items-center gap-1.5">
                      {k.label}
                      {!k.editable && <Lock className="w-3 h-3 text-[#8A857A]" />}
                    </div>
                    <div className="mt-1">
                      {k.set
                        ? <Badge className="bg-[#E3F3E9] text-[#0E6B3F] border-0 text-[10px]">Set {k.masked}</Badge>
                        : <Badge className="bg-[#DAD5CA] text-[#8A857A] border-0 text-[10px]">Not set</Badge>}
                    </div>
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    {k.editable ? (
                      <>
                        <Input
                          type="password"
                          value={drafts[k.name] ?? ""}
                          onChange={(e) => setDrafts(d => ({ ...d, [k.name]: e.target.value }))}
                          placeholder={k.set ? "Enter new value to replace…" : "Paste key/value…"}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid={`key-input-${k.name}`} />
                        <Button size="sm" onClick={() => save(k.name)} disabled={saving === k.name || drafts[k.name] === undefined}
                          className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] shrink-0" data-testid={`key-save-${k.name}`}>
                          {saving === k.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-[#8A857A]">Managed via environment</span>
                    )}
                    {k.test && (
                      <Button size="sm" variant="outline" onClick={() => test(k.test)} disabled={testing === k.test}
                        className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] shrink-0" data-testid={`key-test-${k.test}`}>
                        {testing === k.test ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />} Test
                      </Button>
                    )}
                    {tr && (tr.ok
                      ? <CheckCircle2 className="w-5 h-5 text-[#0E6B3F]" data-testid={`key-test-ok-${k.test}`} />
                      : <span className="inline-flex items-center gap-1 text-[#C23A00] text-xs" title={tr.detail} data-testid={`key-test-fail-${k.test}`}><XCircle className="w-4 h-4" /> failed</span>)}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ))}

      {adStatus && (
        <div className="space-y-2" data-testid="ad-connection-status">
          <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Ad Platform Connection</div>
          <p className="text-[11px] text-[#8A857A] -mt-1">Saving keys is step 1. LinkedIn also needs a one-time <b className="text-[#59554D]">Connect</b> (OAuth) before data can be pulled. Meta connects automatically once its System User token is saved.</p>
          <AdStatusRow name="Meta" icon={Facebook} tone="#8A6D00" cfg={adStatus.meta}
            onRefresh={() => syncAds("meta")} busy={adBusy === "meta"} />
          <AdStatusRow name="LinkedIn" icon={Linkedin} tone="#0A66C2" cfg={adStatus.linkedin}
            onConnect={connectLinkedIn} onRefresh={() => syncAds("linkedin")} busy={adBusy === "linkedin"} />
          {adStatus.linkedin?.redirect_uri && (
            <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3" data-testid="ad-redirect-uri">
              <div className="text-[11px] text-[#8A857A] mb-1">Register this exact <b className="text-[#59554D]">Authorized redirect URL</b> in your LinkedIn Developer app (Auth tab):</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] text-[#8A6D00] bg-[#FFFFFF] border border-[#DAD5CA] rounded px-2 py-1.5 truncate">{adStatus.linkedin.redirect_uri}</code>
                <Button size="sm" variant="outline" onClick={copyRedirect} className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] shrink-0" data-testid="ad-redirect-copy">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
              <div className="text-[11px] text-[#8A857A] mt-1.5">The Connect handshake completes on your production domain — the popup must land on this URL.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
